import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type {
  KimiChatContextMetadata,
  KimiChatMessage,
  PendingActionBundle,
  SermonAgentActionResult,
  SermonAgentUploadRequest,
} from '../../services/kimiChat';
import { MAX_CHAT_INPUT_CHARS } from '../../services/kimiChat';

type ChatPanelProps = {
  open: boolean;
  messages: KimiChatMessage[];
  isLoading: boolean;
  error: string | null;
  contextMetadata?: KimiChatContextMetadata | null;
  uploadRequests: SermonAgentUploadRequest[];
  uploadingActionId?: string | null;
  pendingActionBundle?: PendingActionBundle | null;
  onUploadFile: (request: SermonAgentUploadRequest, file: File) => Promise<void> | void;
  onConfirmPendingChanges: () => Promise<void> | void;
  onCancelPendingChanges: () => void;
  onSend: (text: string, attachments: File[]) => Promise<void> | void;
  onClose: () => void;
};

/* ~74px FAB + gap; keep max-height independent to avoid invalid nested calc() */
const Panel = styled.section<{ $open: boolean }>`
  /* override global section { padding: 90px 0 } and mobile 60px/40px */
  padding: 0;
  position: fixed;
  z-index: 1149;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
  min-height: 0;

  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  width: min(440px, calc(100vw - 2 * max(16px, env(safe-area-inset-left, 0px))));
  height: min(
    560px,
    calc(100dvh - 88px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 32px)
  );
  max-height: min(
    560px,
    calc(100dvh - 88px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 32px)
  );

  border-radius: 20px;
  background: linear-gradient(145deg, rgba(58, 58, 58, 0.98) 0%, rgba(36, 36, 36, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.35) inset,
    0 24px 48px rgba(0, 0, 0, 0.55),
    0 8px 16px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);

  @media (max-width: 600px) {
    left: max(12px, env(safe-area-inset-left, 0px));
    right: max(12px, env(safe-area-inset-right, 0px));
    width: auto;
    bottom: calc(88px + env(safe-area-inset-bottom, 0px));
    height: min(
      72dvh,
      calc(100dvh - 88px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 16px)
    );
    max-height: min(
      72dvh,
      calc(100dvh - 88px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 16px)
    );
    border-radius: 22px;
  }

  @media (max-width: 380px) {
    border-radius: 18px;
  }
`;

const Header = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, transparent 100%);
`;

const HeaderLead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
`;

const HeaderIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 188, 212, 0.35), rgba(215, 196, 173, 0.2));
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  line-height: 1;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const Title = styled.strong`
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.span`
  font-size: 0.75rem;
  color: var(--muted);
  line-height: 1.3;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
  color: var(--muted);
  border-radius: 10px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  font-size: 1.15rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--text);
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.14);
  }
`;

const MessageList = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0.85rem 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MessageGroup = styled.div<{ $role: KimiChatMessage['role'] }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-width: min(92%, 360px);
`;

const MessageBubble = styled.div<{ $role: KimiChatMessage['role'] }>`
  border-radius: ${({ $role }) =>
    $role === 'user' ? '16px 16px 6px 16px' : '16px 16px 16px 6px'};
  padding: 0.65rem 0.85rem;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  font-size: 0.9rem;
  background: ${({ $role }) =>
    $role === 'user'
      ? 'linear-gradient(145deg, var(--primary) 0%, var(--primaryDark) 120%)'
      : 'rgba(255, 255, 255, 0.06)'};
  color: ${({ $role }) => ($role === 'user' ? 'var(--onPrimary)' : 'var(--text)')};
  border: ${({ $role }) =>
    $role === 'user' ? 'none' : '1px solid rgba(255, 255, 255, 0.07)'};
  box-shadow: ${({ $role }) =>
    $role === 'user' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'};
`;

const ReadResultsWrap = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const ReadResultCard = styled.article`
  border-radius: 14px;
  border: 1px solid rgba(0, 188, 212, 0.22);
  background: linear-gradient(145deg, rgba(0, 188, 212, 0.08), rgba(255, 255, 255, 0.03));
  padding: 0.75rem 0.8rem;
  display: grid;
  gap: 0.55rem;
`;

const ReadResultHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ReadResultTitleWrap = styled.div`
  min-width: 0;
  display: grid;
  gap: 0.18rem;
`;

const ReadResultEyebrow = styled.div`
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 188, 212, 0.95);
`;

const ReadResultTitle = styled.div`
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text);
`;

const ReadResultSummary = styled.div`
  font-size: 0.76rem;
  color: var(--muted);
  line-height: 1.35;
`;

const StatusPill = styled.span<{ $tone: 'success' | 'muted' }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
  background: ${({ $tone }) =>
    $tone === 'success' ? 'rgba(26, 143, 92, 0.18)' : 'rgba(255, 255, 255, 0.08)'};
  color: ${({ $tone }) => ($tone === 'success' ? 'var(--success, #76d39c)' : 'var(--muted)')};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? 'rgba(26, 143, 92, 0.26)' : 'rgba(255, 255, 255, 0.1)')};
`;

const AggregateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const AggregatePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  border-radius: 999px;
  padding: 0.22rem 0.52rem;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--text);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.4rem;
`;

const InfoCard = styled.div`
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 0.5rem 0.55rem;
  display: grid;
  gap: 0.12rem;
`;

const InfoLabel = styled.div`
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
`;

const InfoValue = styled.div`
  font-size: 0.76rem;
  color: var(--text);
  line-height: 1.3;
  word-break: break-word;
`;

const ReadPreviewList = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const ReadPreviewRow = styled.div`
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 0.5rem 0.55rem;
  display: grid;
  gap: 0.15rem;
`;

const ReadPreviewTitle = styled.div`
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--text);
`;

const ReadPreviewMeta = styled.div`
  font-size: 0.7rem;
  color: var(--muted);
  line-height: 1.3;
`;

const ErrorBanner = styled.div`
  flex-shrink: 0;
  margin: 0 1rem 0.5rem;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  background: rgba(207, 102, 121, 0.15);
  border: 1px solid rgba(207, 102, 121, 0.35);
  color: var(--onErrorContainer);
  font-size: 0.8rem;
  line-height: 1.35;
`;

const UploadRequestsWrap = styled.div`
  flex-shrink: 0;
  margin: 0 1rem 0.6rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(0, 188, 212, 0.25);
  background: rgba(0, 188, 212, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PendingChangesWrap = styled.div`
  flex-shrink: 0;
  margin: 0 1rem 0.6rem;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 193, 7, 0.28);
  background: rgba(255, 193, 7, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const PendingTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text);
`;

const PendingList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  display: grid;
  gap: 0.25rem;
  color: var(--text);
  font-size: 0.76rem;
  line-height: 1.35;
`;

const PendingMeta = styled.div`
  font-size: 0.72rem;
  color: var(--muted);
`;

const PendingActionsRow = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const PendingButton = styled.button<{ $variant?: 'danger' | 'primary' }>`
  border: 1px solid
    ${({ $variant }) => ($variant === 'danger' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 193, 7, 0.5)')};
  border-radius: 8px;
  padding: 0.38rem 0.8rem;
  cursor: pointer;
  font-size: 0.74rem;
  font-weight: 600;
  background: ${({ $variant }) => ($variant === 'danger' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 193, 7, 0.18)')};
  color: var(--text);

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const UploadRequestCard = styled.div`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  background: rgba(0, 0, 0, 0.2);
`;

const UploadTitle = styled.div`
  font-size: 0.78rem;
  color: var(--text);
  font-weight: 600;
`;

const UploadHint = styled.div`
  font-size: 0.74rem;
  color: var(--muted);
  line-height: 1.35;
`;

const UploadInputRow = styled.div`
  display: flex;
  gap: 0.45rem;
  align-items: center;
  flex-wrap: wrap;
`;

const UploadButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.75rem;
  font-weight: 600;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const InputArea = styled.form`
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  background: rgba(0, 0, 0, 0.22);
`;

const Input = styled.textarea`
  width: 100%;
  min-height: 44px;
  max-height: 140px;
  resize: none;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(18, 18, 18, 0.65);
  color: var(--text);
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.4;
  padding: 0.65rem 0.85rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--muted);
    opacity: 0.85;
  }

  &:focus {
    outline: none;
    border-color: rgba(215, 196, 173, 0.45);
    box-shadow: 0 0 0 3px rgba(215, 196, 173, 0.12);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const InputMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const AttachmentComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const AttachmentPickerInput = styled.input`
  display: none;
`;

const AttachmentButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 10px;
  padding: 0.35rem 0.7rem;
  cursor: pointer;
  font-size: 0.74rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const AttachmentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
  font-size: 0.7rem;
  padding: 0.2rem 0.55rem;
  max-width: 220px;
`;

const AttachmentName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  font-size: 0.82rem;
  line-height: 1;
  padding: 0;
`;

const CharCount = styled.span<{ $danger: boolean }>`
  color: ${({ $danger }) => ($danger ? 'var(--error)' : 'var(--muted)')};
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
`;

const SendButton = styled.button`
  border: none;
  border-radius: 12px;
  background: linear-gradient(145deg, var(--primary) 0%, var(--primaryDark) 100%);
  color: var(--onPrimary);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 1.1rem;
  min-height: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: transform 0.12s ease, opacity 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
`;

const TypingRow = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0.65rem 0.9rem;
  border-radius: 16px 16px 16px 6px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.07);
`;

const TypingDot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--muted);
  animation: ${dotPulse} 1.1s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.75;
  }
`;

const typingIndicatorMessage: KimiChatMessage = {
  role: 'assistant',
  content: '__typing__',
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const formatLabel = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateish = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Date(timestamp).toLocaleString();
};

const previewValue = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
};

const readResultTitle = (type: string): string => {
  switch (type) {
    case 'get_classroom_overview':
      return 'Classroom Overview';
    case 'get_class_detail':
      return 'Class Snapshot';
    case 'get_class_students':
      return 'Class Roster';
    case 'get_class_lessons':
      return 'Lesson List';
    case 'get_lesson_detail':
      return 'Lesson Detail';
    case 'get_class_work':
      return 'Assignments And Quizzes';
    case 'get_work_detail':
      return 'Work Detail';
    case 'get_submission_detail':
      return 'Submission Detail';
    case 'get_class_discussion':
      return 'Discussion Threads';
    case 'get_class_syllabus':
      return 'Syllabus';
    case 'get_class_textbooks':
      return 'Textbooks';
    case 'get_class_analytics':
      return 'Class Analytics';
    case 'search_classroom_entities':
      return 'Classroom Search';
    default:
      return formatLabel(type);
  }
};

const renderAggregatePills = (aggregates: unknown) => {
  if (!isRecord(aggregates)) return null;
  const entries = Object.entries(aggregates)
    .map(([key, value]) => {
      const rendered = previewValue(value);
      return rendered ? { key, rendered } : null;
    })
    .filter((entry): entry is { key: string; rendered: string } => Boolean(entry))
    .slice(0, 8);
  if (!entries.length) return null;
  return (
    <AggregateGrid>
      {entries.map((entry) => (
        <AggregatePill key={entry.key}>
          {formatLabel(entry.key)}: {entry.rendered}
        </AggregatePill>
      ))}
    </AggregateGrid>
  );
};

const renderInfoCards = (details: Record<string, unknown>) => {
  const candidates = [details.class, details.lesson, details.work, details.submission, details.syllabus, details.student]
    .filter(isRecord)
    .slice(0, 3);
  if (!candidates.length) return null;

  const cards = candidates.flatMap((entry) => {
    const preferredKeys = ['name', 'title', 'studentName', 'status', 'score', 'dueAt', 'lessonDate', 'updatedAt'];
    const explicit = preferredKeys
      .map((key) => {
        const value = entry[key];
        const rendered = key.toLowerCase().includes('date') || key.toLowerCase().includes('at') ? formatDateish(value) : previewValue(value);
        return rendered ? { key, rendered } : null;
      })
      .filter((row): row is { key: string; rendered: string } => Boolean(row));
    return explicit.slice(0, 4);
  });

  if (!cards.length) return null;
  return (
    <InfoGrid>
      {cards.slice(0, 6).map((entry, index) => (
        <InfoCard key={`${entry.key}-${index}`}>
          <InfoLabel>{formatLabel(entry.key)}</InfoLabel>
          <InfoValue>{entry.rendered}</InfoValue>
        </InfoCard>
      ))}
    </InfoGrid>
  );
};

const renderPreviewRows = (result: SermonAgentActionResult) => {
  const details = isRecord(result.details) ? result.details : null;
  if (!details) return null;

  const rows: Array<{ title: string; meta?: string | null }> = [];

  const records = Array.isArray(details.records) ? details.records.filter(isRecord).slice(0, 4) : [];
  records.forEach((record) => {
    const title =
      previewValue(record.name) ??
      previewValue(record.title) ??
      previewValue(record.fullName) ??
      previewValue(record.studentName) ??
      previewValue(record.id);
    if (!title) return;
    const metaParts = [
      previewValue(record.workType),
      previewValue(record.status),
      formatDateish(record.dueAt),
      formatDateish(record.lessonDate),
      previewValue(record.email),
      previewValue(record.pageCount),
    ].filter(Boolean);
    rows.push({ title, meta: metaParts.join(' · ') || null });
  });

  const blocks = Array.isArray(details.blocks) ? details.blocks.filter(isRecord).slice(0, 4) : [];
  blocks.forEach((block) => {
    const title = previewValue(block.title) ?? 'Untitled block';
    const metaParts = [previewValue(block.type), previewValue(block.meta), previewValue(block.quizTitle), previewValue(block.assignmentTitle)].filter(Boolean);
    rows.push({ title, meta: metaParts.join(' · ') || null });
  });

  const posts = Array.isArray(details.posts) ? details.posts.filter(isRecord).slice(0, 3) : [];
  posts.forEach((post) => {
    const title = previewValue(post.authorName) ?? 'Discussion post';
    const body = previewValue(post.body);
    const status = previewValue(post.status);
    rows.push({ title, meta: [status, body].filter(Boolean).join(' · ') || null });
  });

  const answers = isRecord(details.answersByQuestionId)
    ? Object.entries(details.answersByQuestionId).slice(0, 4).map(([key, value]) => ({ title: formatLabel(key), meta: previewValue(value) }))
    : [];
  answers.forEach((answer) => {
    if (!answer.meta) return;
    rows.push({ title: answer.title, meta: answer.meta });
  });

  if (!rows.length) return null;
  return (
    <ReadPreviewList>
      {rows.slice(0, 5).map((row, index) => (
        <ReadPreviewRow key={`${row.title}-${index}`}>
          <ReadPreviewTitle>{row.title}</ReadPreviewTitle>
          {row.meta ? <ReadPreviewMeta>{row.meta}</ReadPreviewMeta> : null}
        </ReadPreviewRow>
      ))}
    </ReadPreviewList>
  );
};

const renderClassroomReadCards = (results: SermonAgentActionResult[] | undefined) => {
  if (!results?.length) return null;
  return (
    <ReadResultsWrap>
      {results.map((result) => {
        const details = isRecord(result.details) ? result.details : {};
        return (
          <ReadResultCard key={result.actionId}>
            <ReadResultHead>
              <ReadResultTitleWrap>
                <ReadResultEyebrow>Classroom insight</ReadResultEyebrow>
                <ReadResultTitle>{readResultTitle(result.type)}</ReadResultTitle>
                <ReadResultSummary>{result.summary}</ReadResultSummary>
              </ReadResultTitleWrap>
              <StatusPill $tone="success">Ready</StatusPill>
            </ReadResultHead>
            {renderAggregatePills(details.aggregates)}
            {renderInfoCards(details)}
            {renderPreviewRows(result)}
          </ReadResultCard>
        );
      })}
    </ReadResultsWrap>
  );
};

const ChatPanel: React.FC<ChatPanelProps> = ({
  open,
  messages,
  isLoading,
  error,
  contextMetadata,
  uploadRequests,
  uploadingActionId,
  pendingActionBundle,
  onUploadFile,
  onConfirmPendingChanges,
  onCancelPendingChanges,
  onSend,
  onClose,
}) => {
  const [draft, setDraft] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [selectedFilesByActionId, setSelectedFilesByActionId] = useState<Record<string, File | null>>({});
  const messageListRef = useRef<HTMLDivElement>(null);
  const attachmentPickerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const list = messageListRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [open, messages, isLoading]);

  const count = draft.length;
  const canSend =
    !isLoading &&
    !pendingActionBundle &&
    (Boolean(draft.trim()) || selectedAttachments.length > 0) &&
    count <= MAX_CHAT_INPUT_CHARS;
  const renderedMessages = useMemo(
    () => (isLoading ? [...messages, typingIndicatorMessage] : messages),
    [messages, isLoading]
  );
  const subtitle = useMemo(() => {
    if (!contextMetadata) return 'Church dashboard · signed-in only';
    const scope = contextMetadata.roleScope === 'elevated' ? 'elevated context active' : 'member context active';
    return `Church dashboard · ${scope}`;
  }, [contextMetadata]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSend) return;
    const trimmed = draft.trim();
    const nextAttachments = [...selectedAttachments];
    setDraft('');
    setSelectedAttachments([]);
    if (attachmentPickerRef.current) {
      attachmentPickerRef.current.value = '';
    }
    await onSend(trimmed, nextAttachments);
  };

  const getUploadTitle = (target: SermonAgentUploadRequest['target']) => {
    if (target === 'confession_image') return 'Confession image';
    if (target === 'points_image') return 'Points image';
    return 'PDF notes document';
  };

  return (
    <Panel $open={open} aria-hidden={!open} aria-label="NCOF assistant chat panel">
      <Header>
        <HeaderLead>
          <HeaderIcon aria-hidden="true">💬</HeaderIcon>
          <HeaderTitle>
            <Title>NCOF App Assistant</Title>
            <Subtitle>{subtitle}</Subtitle>
          </HeaderTitle>
        </HeaderLead>
        <CloseButton type="button" onClick={onClose} aria-label="Close chat panel">
          ×
        </CloseButton>
      </Header>

      <MessageList ref={messageListRef}>
        {renderedMessages.length ? (
          renderedMessages.map((message, index) =>
            message.content === '__typing__' ? (
              <TypingRow key={`typing-${index}`} aria-live="polite" aria-label="Assistant is typing">
                <TypingDot $delay={0} />
                <TypingDot $delay={160} />
                <TypingDot $delay={320} />
              </TypingRow>
            ) : (
              <MessageGroup key={`${message.role}-${index}`} $role={message.role}>
                <MessageBubble $role={message.role}>{message.content}</MessageBubble>
                {message.role === 'assistant' ? renderClassroomReadCards(message.classroomReadResults) : null}
              </MessageGroup>
            )
          )
        ) : (
          <MessageBubble $role="assistant">
            Ask me about planning, event workflows, communication drafts, or admin tasks.
          </MessageBubble>
        )}
      </MessageList>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      {pendingActionBundle ? (
        <PendingChangesWrap>
          <PendingTitle>Review and confirm changes</PendingTitle>
          <PendingList>
            {(pendingActionBundle.draftChanges.length ? pendingActionBundle.draftChanges : ['Apply requested updates.']).map(
              (item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              )
            )}
          </PendingList>
          <PendingMeta>
            {pendingActionBundle.actionCount} action{pendingActionBundle.actionCount === 1 ? '' : 's'} pending · expires{' '}
            {new Date(pendingActionBundle.expiresAt).toLocaleTimeString()}
          </PendingMeta>
          <PendingActionsRow>
            <PendingButton type="button" onClick={() => onConfirmPendingChanges()} disabled={isLoading}>
              {isLoading ? 'Applying…' : 'Confirm changes'}
            </PendingButton>
            <PendingButton type="button" $variant="danger" onClick={onCancelPendingChanges} disabled={isLoading}>
              Cancel
            </PendingButton>
          </PendingActionsRow>
        </PendingChangesWrap>
      ) : null}

      {uploadRequests.length ? (
        <UploadRequestsWrap>
          <UploadTitle>Pending uploads</UploadTitle>
          {uploadRequests.map((request) => {
            const selected = selectedFilesByActionId[request.actionId] ?? null;
            const isUploading = uploadingActionId === request.actionId;
            return (
              <UploadRequestCard key={request.actionId}>
                <UploadHint>
                  {getUploadTitle(request.target)} for sermon `{request.sermonId.slice(0, 8)}`.
                </UploadHint>
                <UploadInputRow>
                  <Input
                    as="input"
                    type="file"
                    disabled={isUploading}
                    onChange={(event) => {
                      const next = event.target.files?.[0] ?? null;
                      setSelectedFilesByActionId((previous) => ({
                        ...previous,
                        [request.actionId]: next,
                      }));
                    }}
                  />
                  <UploadButton
                    type="button"
                    disabled={!selected || isUploading}
                    onClick={async () => {
                      if (!selected) return;
                      await onUploadFile(request, selected);
                      setSelectedFilesByActionId((previous) => ({
                        ...previous,
                        [request.actionId]: null,
                      }));
                    }}
                  >
                    {isUploading ? 'Uploading…' : 'Upload'}
                  </UploadButton>
                </UploadInputRow>
              </UploadRequestCard>
            );
          })}
        </UploadRequestsWrap>
      ) : null}

      <InputArea onSubmit={handleSubmit}>
        <AttachmentComposerRow>
          <AttachmentPickerInput
            ref={attachmentPickerRef}
            type="file"
            multiple
            disabled={isLoading || Boolean(pendingActionBundle)}
            onChange={(event) => {
              const selected = event.target.files ? Array.from(event.target.files) : [];
              setSelectedAttachments(selected.slice(0, 3));
            }}
          />
          <AttachmentButton
            type="button"
            disabled={isLoading || Boolean(pendingActionBundle)}
            onClick={() => attachmentPickerRef.current?.click()}
          >
            {selectedAttachments.length ? 'Change attachment' : 'Attach file/image'}
          </AttachmentButton>
          {selectedAttachments.length ? (
            <AttachmentList>
              {selectedAttachments.map((file) => (
                <AttachmentChip key={`${file.name}-${file.size}`}>
                  <AttachmentName title={file.name}>{file.name}</AttachmentName>
                  <AttachmentRemoveButton
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      setSelectedAttachments((previous) =>
                        previous.filter((entry) => !(entry.name === file.name && entry.size === file.size))
                      )
                    }
                  >
                    x
                  </AttachmentRemoveButton>
                </AttachmentChip>
              ))}
            </AttachmentList>
          ) : null}
        </AttachmentComposerRow>
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={pendingActionBundle ? 'Confirm or cancel pending changes first...' : 'Type your message...'}
          maxLength={MAX_CHAT_INPUT_CHARS}
          disabled={isLoading || Boolean(pendingActionBundle)}
        />
        <InputMetaRow>
          <CharCount $danger={count > MAX_CHAT_INPUT_CHARS * 0.9}>
            {count}/{MAX_CHAT_INPUT_CHARS}
          </CharCount>
          <SendButton type="submit" disabled={!canSend}>
            {isLoading ? 'Sending…' : 'Send'}
          </SendButton>
        </InputMetaRow>
      </InputArea>
    </Panel>
  );
};

export default ChatPanel;
